import React, { FC, useEffect, useState } from "react";
import { getSectionByID } from "../../Tools/Tools";
import { Autocomplete, IconButton } from "@mui/material";
import Popper from "@mui/material/Popper";
import CloseIcon from "@mui/icons-material/Close";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { currentTheme, setSelectedSectionAtom } from "../../../../atoms";
import { SectionsList, SectionsListDocument, SectionsListPages, SectionsListReport } from "./SideBarInterface";
import sideBarStyles from "./sidebar.module.css";

const styles = (theme) => ({
  popper: {
    width: "fit-content",
  },
});

const PopperMy = function (props) {
  return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

interface SmetaSectionSearchInterface {
  background?: boolean;
  sectionArr: SectionsList[];
}

interface SmetaSectionSearchSelectInterface {
  Name: string;
  selected: {
    docCfgId?: string;
    name: string;
    reportId?: string;
    path?: string;
    clsid?: string;
    progId?: string;
  };
  sectionId: string;
}

interface SmetaParentSectionInfo {
  name: string;
  id: string;
}

//eslint-disable-next-line
const SectionSearch: FC<SmetaSectionSearchInterface> = ({ background = false, sectionArr }) => {
  const theme = useAtomValue(currentTheme);
  const [searchData, setSearchData] = useState<SmetaSectionSearchSelectInterface[]>([]);
  const [value, setValue] = useState<SmetaSectionSearchSelectInterface | object>({});
  const [inputValue, setInputValue] = useState("");
  const setSelectedSection = useSetAtom(setSelectedSectionAtom);

  useEffect(() => {
    if (searchData.length > 0) return;
    const data = createSearchData();
    setSearchData(data);
  }, [inputValue]);

  const createSearchData = () => {
    const isEmptyArray = (arr: []) => !Array.isArray(arr) || arr.length === 0;
    if (!sectionArr || isEmptyArray(sectionArr)) return [];

    const buildPath = (parent: string, name: string) => `${parent}/${name}`;

    const parseReports = (
      items: SectionsListReport[],
      section: SmetaParentSectionInfo,
    ): SmetaSectionSearchSelectInterface[] => {
      if (isEmptyArray(items)) return [];

      return items.flatMap(({ Name: reportName, ID: reportId, items: children }) => {
        const name = buildPath(section.name, reportName);

        return [
          ...parseReports(children || [], { ...section, name }),
          {
            Name: name,
            selected: {
              reportId,
              name: reportName,
            },
            sectionId: section.id,
          },
        ];
      });
    };

    const parseDocs = (items: SectionsListDocument[], section: SmetaParentSectionInfo) => {
      if (isEmptyArray(items)) return [];

      return items.map(({ Name: docName, DocCfgID: docCfgId }) => ({
        Name: buildPath(section.name, docName),
        selected: {
          DocCfgID: docCfgId,
          name: docName,
        },
        sectionId: section.id,
      }));
    };

    const parsePages = (items: SectionsListPages[], section: SmetaParentSectionInfo) => {
      if (isEmptyArray(items)) return [];

      return items.map(({ Name: pageName, CLSID: clsid, Path: path, ProgID: progId }) => ({
        selected: {
          path,
          clsid,
          progId,
          name: pageName,
        },
        Name: buildPath(section.name, pageName),
        sectionId: section.id,
      }));
    };

    const parseSections = (sections: SectionsList[], parentName = ""): SmetaSectionSearchSelectInterface[] => {
      if (isEmptyArray(sections)) return [];

      return sections.flatMap(
        ({ Sections: sections, Name: name, Documents: documents, Reports: reports, Pages: pages, ID: sectionId }) => {
          const currentName = parentName === "" ? name : buildPath(parentName, name);
          const sectionInfo = { name: currentName, id: sectionId };
          return [
            ...parseSections(sections || [], currentName),
            ...parseReports(reports || [], sectionInfo),
            ...parseDocs(documents || [], sectionInfo),
            ...parsePages(pages || [], sectionInfo),
            { sectionId: sectionId, Name: currentName },
          ];
        },
      );
    };

    const searchData = parseSections(sectionArr);
    return searchData || [];
  };

  const selectValue = (selectedItem: SmetaSectionSearchSelectInterface) => {
    const sectionInfo = getSectionByID(selectedItem.sectionId);
    setSelectedSection({ ...sectionInfo, Selected: selectedItem.selected });
  };

  function clearValue() {
    setValue({});
    setInputValue("");
    if (value === "") document.getElementById("MainSplitter").collapse(true);
  }

  return (
    <div id={"SearchBlock"} className={clsx(sideBarStyles.SearchBlock)}>
      <Autocomplete
        size="small"
        disableListWrap
        clearOnBlur
        handleHomeEndKeys
        sx={{
          display: "inline-block",
          width: "100%",
          ml: 1,
        }}
        value={value}
        onChange={(event, newValue: SmetaSectionSearchSelectInterface) => {
          selectValue(newValue);
          setValue(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        noOptionsText="Ничего не найдено :c"
        id="Search"
        options={searchData}
        // freeSolo
        slots={{ popper: PopperMy }}
        renderInput={(params) => (
          <div className={clsx(sideBarStyles.SearchInputWrapper)} ref={params.InputProps.ref}>
            <input
              {...params.inputProps}
              type="text"
              className={clsx(sideBarStyles.SearchInput, {
                [sideBarStyles.SearchInputWithBackground]: !!background,
              })}
              placeholder="Поиск..."
            />
          </div>
        )}
        renderOption={(props, option, { inputValue }) => {
          const text = option.Name;
          const matches = match(text, inputValue);
          const parts = parse(text, matches);

          return (
            <li {...props} key={`${props.key}_${props.id}`}>
              <div>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            </li>
          );
        }}
        getOptionLabel={(option) => option.Name || ""}
      />
      <IconButton onClick={clearValue}>
        <CloseIcon className={clsx("iconButtonStimate", { light: theme === "light" })} fontSize="small" />
      </IconButton>
    </div>
  );
};

export default SectionSearch;
